/**
 * PermissionGate — Wrapper für Bereiche, die ein Recht voraussetzen.
 *
 * Gebaut wie {@link FeatureGate}, nur entscheidet hier das Benutzerrecht statt der Lizenz.
 *
 * 🚨 **Eine ausgeblendete Route ist kein Schutz.** Wer die Adresse kennt, ruft sie trotzdem auf,
 * und die API antwortet unabhängig davon. Diese Komponente sorgt dafür, dass niemand versehentlich
 * in einem Bereich landet, der ihn nichts angeht — der eigentliche Riegel sitzt im Server
 * (`TenantSettingsController` verweigert das Speichern ohne `settings.edit`).
 *
 * Usage:
 *   <PermissionGate module="settings" operation="edit">
 *     <SettingsRoutes />
 *   </PermissionGate>
 */

import { Lock } from 'lucide-react';
import { usePermission, MODULES, type PermissionModule, type PermissionOperation } from '../../hooks/usePermission';

/**
 * Wie das fehlende Recht in der Standardmeldung heißt — je Operation. Vorher stand dort immer
 * „bearbeiten“, auch beim reinen Ansehen-Recht (V-0206): Wer „Kunden ansehen“ nicht hat, las dann
 * „Für diesen Bereich fehlt die Berechtigung Kunden bearbeiten“ und bat um das falsche Recht.
 */
const OPERATION_VERB: Record<PermissionOperation, string> = {
  view: 'ansehen',
  create: 'anlegen',
  edit: 'bearbeiten',
  delete: 'löschen',
  export: 'exportieren',
  sync: 'synchronisieren',
};

interface PermissionGateProps {
  module: PermissionModule;
  /** Standard `view` — für Bereiche, die man nur mit Schreibrecht betreten soll, `edit`. */
  operation?: PermissionOperation;
  /** Eigene Ersatzdarstellung statt des Standard-Hinweises. */
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

export function PermissionGate({ module, operation = 'view', fallback, children }: PermissionGateProps) {
  const { hasPermission } = usePermission();

  if (hasPermission(module, operation)) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  const modulName = MODULES[module]?.name ?? module;
  const rechtName = `${modulName} ${OPERATION_VERB[operation] ?? OPERATION_VERB.edit}`;

  return (
    <div className="flex items-center justify-center p-8">
      <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-800">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-700">
          <Lock size={22} />
        </div>
        <h2 className="mb-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
          Kein Zugriff auf {modulName}
        </h2>
        {/* Kein blosses „Zugriff verweigert": Wer hier landet, soll wissen, was ihm fehlt und wen
            er fragen muss — sonst landet die Frage als Störung beim Support. */}
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Für diesen Bereich fehlt die Berechtigung <strong>{rechtName}</strong>.
          Ein Administrator kann sie unter <em>Einstellungen → Benutzer &amp; Rollen</em> vergeben.
        </p>
      </div>
    </div>
  );
}

export default PermissionGate;
